import { FlashcardIcon } from '../../icons/custom/FlashcardIcon';
import { AudioLines } from '../../icons/lucide/AudioLines';
import { ClipboardList } from '../../icons/lucide/ClipboardList';
import { Files } from '../../icons/lucide/Files';
import { SquareCheckBig } from '../../icons/lucide/SquareCheckBig';
import type { CardBadge } from './CardBadge';

/** The four standardized study-material counts every DS card chips out. */
export interface StudyActivityCounts {
  flashcards: number;
  quizzes: number;
  oralExams: number;
  caseStudies: number;
  /**
   * The files a STUDY SET holds (`deckFileCount`, `@flashka/shared/deck/sources`).
   * Deck tiles pass it; folders and groups leave it out and get no files chip.
   */
  files?: number;
}

/**
 * The DS-standard activity chip row for DeckCard / FolderCard /
 * StudyGroupCard: flashcards (custom flashcard glyph), quizzes
 * (SquareCheckBig), oral exams (AudioLines), case studies (ClipboardList) —
 * in that order. Zero-count chips are hidden by `CardChips`; when ALL of
 * them are zero the cards fall back to their `emptyLabel` badge ("no study
 * material yet").
 *
 * A study set's FILES chip (stacked documents + count) leads the row when
 * `counts.files` is given: a set that holds documents but nothing generated
 * from them yet says how many it holds instead of reading as empty.
 *
 * `labels` are the full localized, pluralized strings ("4 quizzes",
 * "1 flashcard") — shown in each chip's hover tooltip and used as the
 * chip's accessibility name.
 */
export function studyActivityBadges(
  counts: StudyActivityCounts,
  labels: StudyActivityLabels,
): CardBadge[] {
  return [
    ...(counts.files === undefined
      ? []
      : [{ count: counts.files, icon: Files, label: labels.files, leading: true }]),
    { count: counts.flashcards, icon: FlashcardIcon, label: labels.flashcards },
    { count: counts.quizzes, icon: SquareCheckBig, label: labels.quizzes },
    { count: counts.oralExams, icon: AudioLines, label: labels.oralExams },
    { count: counts.caseStudies, icon: ClipboardList, label: labels.caseStudies },
  ];
}

export interface StudyActivityLabels {
  flashcards: string;
  quizzes: string;
  oralExams: string;
  caseStudies: string;
  /** "3 files" — pass it whenever `counts.files` is passed. */
  files?: string;
}
